import { useState } from 'react';
import { ChevronDown, ChevronUp, Car, User, CreditCard, Shield, MessageCircle, Calendar, FileText, FileCheck, AlertTriangle, HelpCircle, Award } from 'lucide-react';

export default function HowItWorks() {
  const [openSection, setOpenSection] = useState('overview');

  const toggleSection = (section) => {
    if (openSection === section) {
      setOpenSection('');
    } else {
      setOpenSection(section);
    }
  };

  const Section = ({ id, title, icon, children }) => {
    const isOpen = openSection === id;
    return (
      <div className="mb-6 border rounded-lg overflow-hidden shadow-sm">
        <button 
          className="w-full flex items-center justify-between p-4 bg-primary-50 hover:bg-primary-100 transition-colors"
          onClick={() => toggleSection(id)}
        >
          <div className="flex items-center">
            {icon}
            <h2 className="text-xl font-semibold ml-2">{title}</h2>
          </div>
          {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
        {isOpen && (
          <div className="p-4 bg-white">
            {children}
          </div>
        )}
      </div>
    );
  };

  const SubSection = ({ title, children }) => (
    <div className="mb-4">
      <h3 className="text-lg font-medium mb-2 text-primary-700">{title}</h3>
      <div className="pl-2 border-l-2 border-primary-200">
        {children}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-primary-800">RideMe - How It Works</h1>
        <p className="text-gray-600 mt-2">Your comprehensive guide to the RideMe peer-to-peer car rental platform</p>
      </div>

      <Section id="overview" title="Platform Overview" icon={<Award className="h-6 w-6 text-primary-600" />}>
        <p className="mb-4">
          RideMe is a peer-to-peer car rental platform designed to connect car providers with customers looking to rent vehicles. 
          Our platform facilitates the entire rental process, from finding the perfect vehicle to handling payments securely.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-primary-50 p-4 rounded-lg text-center">
            <Car className="h-8 w-8 mx-auto text-primary-600 mb-2" />
            <h3 className="font-medium">Diverse Fleet</h3>
            <p className="text-sm">Access to a wide range of vehicles</p>
          </div>
          <div className="bg-primary-50 p-4 rounded-lg text-center">
            <Shield className="h-8 w-8 mx-auto text-primary-600 mb-2" />
            <p className="font-medium">Secure Platform</p>
            <p className="text-sm">KYC verification for all users</p>
          </div>
          <div className="bg-primary-50 p-4 rounded-lg text-center">
            <CreditCard className="h-8 w-8 mx-auto text-primary-600 mb-2" />
            <p className="font-medium">Easy Payments</p>
            <p className="text-sm">Seamless booking and payment process</p>
          </div>
        </div>
        <SubSection title="System Requirements">
          <ul className="list-disc pl-5 space-y-1">
            <li>Mobile app available for iOS (12.0+) and Android (8.0+)</li>
            <li>Stable internet connection required for optimal experience</li>
            <li>GPS functionality needed for location-based features</li>
          </ul>
        </SubSection>
        
        <SubSection title="Feedback System">
          <ul className="list-disc pl-5">
            <li>Rate and review after each rental experience</li>
            <li>Provide detailed feedback on platform features</li>
            <li>Submit enhancement suggestions</li>
            <li>Participate in user research and beta testing</li>
          </ul>
        </SubSection>
      </Section>

      <Section id="authentication" title="Authentication" icon={<User className="h-6 w-6 text-primary-600" />}>
        <SubSection title="User Types">
          <ul className="list-disc pl-5">
            <li><strong>Customers:</strong> Users who rent cars from providers</li>
            <li><strong>Car Providers:</strong> Users who list and manage cars for rent (can also perform customer functions)</li>
          </ul>
        </SubSection>

        <SubSection title="Login Methods">
          <ul className="list-disc pl-5">
            <li><strong>Phone Number:</strong> OTP verification sent to your mobile</li>
            <li><strong>Email:</strong> OTP verification sent to your email address</li>
            <li><strong>Apple Login:</strong> Secure sign-in with your Apple account</li>
            <li><strong>Google Login:</strong> Secure sign-in with your Google account</li>
          </ul>
        </SubSection>

        <SubSection title="Authentication Flow">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Select your preferred login method from the options provided</li>
            <li>For email/phone sign-ins:
              <ul className="list-disc pl-5 mt-1">
                <li>Enter your email address or phone number</li>
                <li>Receive an OTP (One-Time Password) via SMS or email</li>
                <li>Enter the OTP to verify your identity</li>
                <li>Existing users will be logged in automatically</li>
                <li>New users will be prompted to complete the onboarding process</li>
              </ul>
            </li>
            <li>For Apple/Google login:
              <ul className="list-disc pl-5 mt-1">
                <li>Select the respective login option</li>
                <li>Authorize RideMe to access your basic profile information</li>
                <li>Complete or update your profile information as required</li>
              </ul>
            </li>
          </ol>
        </SubSection>

        <SubSection title="Account Security">
          <p className="mb-2">We implement multiple security measures to protect your account:</p>
          <ul className="list-disc pl-5">
            <li>Two-factor authentication available for additional security</li>
            <li>Automatic detection of suspicious login attempts</li>
            <li>Options to manage logged-in devices</li>
            <li>Regular security audits and updates</li>
          </ul>
        </SubSection>
      </Section>

      <Section id="kyc" title="KYC (Know Your Customer)" icon={<FileCheck className="h-6 w-6 text-primary-600" />}>
        <SubSection title="Purpose">
          <p>
            Our KYC process is designed to prevent fraud and ensure secure transactions for both customers and providers.
            Verification is essential to build trust in our peer-to-peer marketplace.
          </p>
        </SubSection>

        <SubSection title="Required Documents">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b text-left">User Category</th>
                  <th className="py-2 px-4 border-b text-left">Required Documents</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-4 border-b">Ghanaian Users</td>
                  <td className="py-2 px-4 border-b">Driver's License & Ghana Card</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border-b">Non-Ghanaian Users</td>
                  <td className="py-2 px-4 border-b">Driver's License & Passport</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SubSection>

        <SubSection title="Verification Process">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Navigate to the Profile section and select "Verify Identity"</li>
            <li>Select your user category (Ghanaian or Non-Ghanaian)</li>
            <li>Upload clear, high-resolution images of both required documents</li>
            <li>Complete a quick selfie verification to confirm your identity</li>
            <li>Wait for verification confirmation (typically within 24-48 hours)</li>
            <li>Address any verification issues if prompted by our system</li>
          </ol>
        </SubSection>

        <SubSection title="Key Points">
          <ul className="list-disc pl-5">
            <li>Both required documents must be successfully verified for KYC completion</li>
            <li>Full KYC verification is mandatory to:
              <ul className="list-disc pl-5 mt-1">
                <li>List vehicles (providers)</li>
                <li>Book vehicles (customers/providers)</li>
              </ul>
            </li>
            <li>Document expiration dates are tracked - you'll be notified when renewal is needed</li>
            <li>All personal data is encrypted and stored securely in compliance with data protection regulations</li>
          </ul>
        </SubSection>
      </Section>

      <Section id="vehicle-listing" title="Vehicle Listing" icon={<Car className="h-6 w-6 text-primary-600" />}>
        <SubSection title="Eligibility Requirements">
          <ul className="list-disc pl-5">
            <li>Completed KYC verification</li>
            <li>Vehicle must meet our platform standards:
              <ul className="list-disc pl-5 mt-1">
                <li>Not older than 15 years (exceptions may apply for vintage/specialty vehicles)</li>
                <li>Valid insurance coverage</li>
                <li>Current vehicle registration</li>
                <li>Roadworthy certification where applicable</li>
              </ul>
            </li>
          </ul>
        </SubSection>

        <SubSection title="Listing Process">
          <ol className="list-decimal pl-5 space-y-2">
            <li><strong>Add Vehicle Details</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Basic information: make, model, year, color, license plate number</li>
                <li>Vehicle features and specifications</li>
                <li>High-quality photos (exterior and interior)</li>
                <li>Vehicle description and special notes</li>
                <li>Vehicle documents (insurance, registration)</li>
              </ul>
            </li>
            <li><strong>Pricing Configuration</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Standard Pricing: Base rental rate (daily/weekly/monthly)</li>
                <li>Custom Pricing: Additional rates for specific scenarios:
                  <ul className="list-disc pl-5 mt-1">
                    <li>Geographic regions (e.g., outside Accra)</li>
                    <li>Special occasions (e.g., holidays, weekends)</li>
                    <li>Security deposits (if required)</li>
                  </ul>
                </li>
                <li>Mileage limits and extra mileage charges</li>
                <li>Fuel policy settings</li>
              </ul>
            </li>
            <li><strong>Availability Management</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Set overall vehicle availability dates</li>
                <li>Define pickup and return time windows</li>
                <li>Block specific dates when the vehicle is unavailable</li>
                <li>Set minimum and maximum rental duration</li>
              </ul>
            </li>
            <li><strong>Listing Review and Publication</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Review complete listing details</li>
                <li>Submit for approval</li>
                <li>Address any issues flagged during review</li>
                <li>Listing goes live once approved</li>
              </ul>
            </li>
          </ol>
        </SubSection>

        <SubSection title="Listing Management">
          <ul className="list-disc pl-5">
            <li>Update vehicle details, pricing, or availability at any time</li>
            <li>Temporarily deactivate listings when needed</li>
            <li>View listing performance metrics and booking history</li>
            <li>Respond to inquiries from potential customers</li>
            <li>Set automatic responses for common questions</li>
          </ul>
        </SubSection>
      </Section>

      <Section id="booking" title="Booking Process" icon={<Calendar className="h-6 w-6 text-primary-600" />}>
        <SubSection title="Customer Booking Flow">
          <ol className="list-decimal pl-5 space-y-2">
            <li><strong>Vehicle Selection</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Browse available vehicles with filtering options</li>
                <li>View detailed vehicle information and photos</li>
                <li>Check provider ratings and reviews</li>
                <li>Compare multiple vehicles</li>
              </ul>
            </li>
            <li><strong>Booking Configuration</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Select rental period (start and end dates)</li>
                <li>Choose available pickup and return times</li>
                <li>Review pricing breakdown (including any special rates or fees)</li>
                <li>Add optional extras if available</li>
              </ul>
            </li>
            <li><strong>Booking Confirmation</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Review booking details</li>
                <li>Accept terms and conditions</li>
                <li>Complete payment to confirm booking</li>
                <li>Receive booking confirmation</li>
              </ul>
            </li>
          </ol>
          <div className="bg-yellow-50 p-3 rounded-md mt-2">
            <p className="text-sm"><strong>Note:</strong> Unpaid bookings are automatically canceled after a 30-minute grace period.</p>
          </div>
        </SubSection>

        <SubSection title="Pre-Rental Process">
          <ul className="list-disc pl-5">
            <li>Vehicle location shared with customer 2 hours before pickup time</li>
            <li>In-app messaging available for communication between customer and provider</li>
            <li>Both parties receive reminder notifications</li>
            <li>Digital rental agreement available for review</li>
          </ul>
        </SubSection>

        <SubSection title="Pickup & Return">
          <ol className="list-decimal pl-5 space-y-2">
            <li><strong>Vehicle Pickup</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Meet at the designated location during the scheduled time</li>
                <li>Both parties conduct vehicle inspection and document condition</li>
                <li>Take photos within the hosts app for documentation</li>
                <li>Customer and provider both mark the vehicle as "Picked Up" in the app</li>
                <li>Provider receives payment (minus RideMe's service fee) in their wallet</li>
              </ul>
            </li>
            <li><strong>During Rental</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Customer can contact provider through the app if needed</li>
                <li>Emergency support available 24/7</li>
                <li>Optional rental extensions (subject to availability and provider approval)</li>
              </ul>
            </li>
            <li><strong>Vehicle Return</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Meet at the designated return location</li>
                <li>Conduct final vehicle inspection</li>
                <li>Document vehicle condition with photos</li>
                <li>Both parties mark the vehicle as "Returned" in the app</li>
                <li>Complete review and rating process</li>
              </ul>
            </li>
          </ol>
        </SubSection>

        <SubSection title="Car Provider as Customer">
          <p className="mb-2">Car providers can also rent vehicles from other providers with the following differences:</p>
          <ul className="list-disc pl-5">
            <li>Special verification badges shown to indicate provider status</li>
            <li>Access to provider-specific promotional rates where applicable</li>
          </ul>
        </SubSection>

        <SubSection title="Cancellation and Modification Policies">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b text-left">Timeline</th>
                <th className="py-2 px-4 border-b text-left">Customer Cancellation Fee</th>
                <th className="py-2 px-4 border-b text-left">Provider Cancellation Fee</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4 border-b">More than 72 hours before pickup</td>
                <td className="py-2 px-4 border-b">Full refund</td>
                <td className="py-2 px-4 border-b">No penalty</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">24-72 hours before pickup</td>
                <td className="py-2 px-4 border-b">50% refund</td>
                <td className="py-2 px-4 border-b">15% penalty + customer compensation</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">Less than 24 hours before pickup</td>
                <td className="py-2 px-4 border-b">20% refund</td>
                <td className="py-2 px-4 border-b">25% penalty + customer compensation</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">No-show</td>
                <td className="py-2 px-4 border-b">No refund</td>
                <td className="py-2 px-4 border-b">50% penalty + customer compensation</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-3 text-sm">* Booking modifications are subject to availability and may incur adjustment fees.</p>
        </SubSection>
      </Section>

      <Section id="payments" title="Payments & Finances" icon={<CreditCard className="h-6 w-6 text-primary-600" />}>
        <SubSection title="Payment Methods">
          <ul className="list-disc pl-5">
            <li>Credit/Debit Cards (Visa, Mastercard)</li>
            <li>Mobile Money (MTN, Vodafone, AirtelTigo)</li>
          </ul>
        </SubSection>

        <SubSection title="Payment Process">
          <ol className="list-decimal pl-5 space-y-2">
            <li><strong>Booking Confirmation</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Full payment required to confirm bookings</li>
                <li>Security deposit may be required (based on provider settings)</li>
                <li>All transactions are encrypted and secure</li>
              </ul>
            </li>
            <li><strong>Provider Payment</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Provider receives payment once the vehicle pickup is confirmed</li>
                <li>RideMe's service fee (15%) is automatically deducted</li>
                <li>Funds are credited to the provider's RideMe wallet</li>
              </ul>
            </li>
            <li><strong>Wallet Management</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Providers can withdraw funds to their bank account or mobile money</li>
                <li>Processing time: 1-3 business days</li>
                <li>Minimum withdrawal amount: GHS 50</li>
              </ul>
            </li>
          </ol>
        </SubSection>

        <SubSection title="Additional Charges">
          <ul className="list-disc pl-5">
            <li><strong>Provider-Initiated Charges:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Damages (with supporting evidence)</li>
                <li>Late returns (exceeding booking time)</li>
                <li>Excess mileage fees</li>
                <li>Cleaning fees (if vehicle returned extremely dirty)</li>
              </ul>
            </li>
            <li><strong>Charge Resolution Process:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Provider submits charge request with evidence</li>
                <li>Customer is notified and has 48 hours to approve or dispute</li>
                <li>Approved charges are processed automatically</li>
                <li>Disputed charges enter RideMe's resolution process</li>
              </ul>
            </li>
          </ul>
          <div className="bg-yellow-50 p-3 rounded-md mt-2">
            <p className="text-sm"><strong>Note:</strong> Customers are placed under a "soft lockdown" (unable to make new bookings) until extra charges are resolved.</p>
          </div>
        </SubSection>

        <SubSection title="Refunds">
          <ul className="list-disc pl-5">
            <li>Processed according to the cancellation policy</li>
            <li>Issued to the original payment method</li>
            <li>Processing time: 5-7 business days</li>
            <li>Option for immediate refund to RideMe wallet</li>
          </ul>
        </SubSection>
      </Section>

      <Section id="insurance" title="Insurance & Liability" icon={<Shield className="h-6 w-6 text-primary-600" />}>
        <SubSection title="Basic Coverage">
          <p className="mb-2">All rentals include basic insurance coverage provided by our insurance partners:</p>
          <ul className="list-disc pl-5">
            <li>Third-party liability coverage</li>
            <li>Basic collision damage coverage</li>
            <li>Theft protection (with deductible)</li>
          </ul>
        </SubSection>

        <SubSection title="Additional Coverage Options">
          <ul className="list-disc pl-5">
            <li><strong>Premium Protection:</strong> Reduces or eliminates deductibles for damages</li>
            <li><strong>Personal Accident Insurance:</strong> Coverage for driver and passengers</li>
            <li><strong>Extended Roadside Assistance:</strong> Enhanced support for breakdowns</li>
          </ul>
          <p className="mt-2 text-sm">* Additional coverage options can be selected during the booking process for an extra fee.</p>
        </SubSection>

        <SubSection title="Liability Information">
          <ul className="list-disc pl-5">
            <li><strong>Customer Responsibility:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Deductible amount in case of damages (varies by vehicle)</li>
                <li>Full adherence to rental terms and local driving laws</li>
                <li>Reporting accidents or incidents immediately</li>
              </ul>
            </li>
            <li><strong>Provider Responsibility:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Maintaining vehicle in road-worthy condition</li>
                <li>Ensuring valid insurance coverage</li>
                <li>Accurate disclosure of vehicle condition</li>
              </ul>
            </li>
            <li><strong>RideMe's Role:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Verifying insurance documentation</li>
                <li>Mediating in damage disputes</li>
                <li>Processing insurance claims</li>
              </ul>
            </li>
          </ul>
        </SubSection>

        <SubSection title="Claims Process">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Document the incident with photos and details</li>
            <li>Report through the app's "Report Issue" feature within 24 hours</li>
            <li>Submit required documentation</li>
            <li>Claims team review and assessment</li>
            <li>Resolution and payment processing</li>
          </ol>
        </SubSection>
      </Section>

      <Section id="reports" title="Issues & Reports" icon={<AlertTriangle className="h-6 w-6 text-primary-600" />}>
        <SubSection title="Reporting Options">
          <ul className="list-disc pl-5">
            <li><strong>Booking Reports:</strong> 
              <ul className="list-disc pl-5 mt-1">
                <li>Damage disputes</li>
                <li>Pickup/return issues</li>
                <li>Vehicle condition discrepancies</li>
                <li>Communication problems</li>
                <li>Payment concerns</li>
              </ul>
            </li>
            <li><strong>General Reports:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>App functionality issues</li>
                <li>Account problems</li>
                <li>Suggestions for improvement</li>
                <li>Policy questions</li>
              </ul>
            </li>
            <li><strong>Emergency Reports:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Accidents</li>
                <li>Vehicle breakdowns</li>
                <li>Safety concerns</li>
                <li>Urgent booking issues</li>
              </ul>
            </li>
          </ul>
        </SubSection>

        <SubSection title="Reporting Process">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Navigate to "Help & Support" in the app menu</li>
            <li>Select the appropriate report type</li>
            <li>Provide detailed information about the issue</li>
            <li>Upload supporting evidence (photos, screenshots)</li>
            <li>Submit the report</li>
            <li>Receive confirmation and case number</li>
          </ol>
        </SubSection>

        <SubSection title="Handling Process">
          <ul className="list-disc pl-5">
            <li>Reports are assigned priority levels based on urgency</li>
            <li>RideMe support team reviews all reports</li>
            <li>Investigation process may include:
              <ul className="list-disc pl-5 mt-1">
                <li>Communication with both parties</li>
                <li>Review of booking history and evidence</li>
                <li>Analysis of vehicle inspection photos</li>
                <li>Consultation with insurance partners (if applicable)</li>
              </ul>
            </li>
            <li>Resolution actions are taken based on findings</li>
            <li>Both parties are notified of the outcome</li>
          </ul>
        </SubSection>

        <SubSection title="Resolution Timeline">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b text-left">Report Type</th>
                <th className="py-2 px-4 border-b text-left">Initial Response</th>
                <th className="py-2 px-4 border-b text-left">Resolution Target</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4 border-b">Emergency</td>
                <td className="py-2 px-4 border-b">Immediate</td>
                <td className="py-2 px-4 border-b">24 hours</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">Booking-related</td>
                <td className="py-2 px-4 border-b">Within 6 hours</td>
                <td className="py-2 px-4 border-b">3-5 days</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">General</td>
                <td className="py-2 px-4 border-b">Within 24 hours</td>
                <td className="py-2 px-4 border-b">5-7 days</td>
              </tr>
            </tbody>
          </table>
        </SubSection>
      </Section>

      <Section id="support" title="Support & Resources" icon={<HelpCircle className="h-6 w-6 text-primary-600" />}>
        <SubSection title="Customer Support Channels">
          <ul className="list-disc pl-5">
            <li><strong>In-App Support:</strong> Available 24/7 through the Help section</li>
            <li><strong>Email Support:</strong> support@rideme.com (response within 24 hours)</li>
            <li><strong>Phone Support:</strong> +233 XX XXX XXXX (9am-6pm, Monday-Saturday)</li>
            <li><strong>Live Chat:</strong> Available during business hours</li>
            <li><strong>Emergency Hotline:</strong> +233 XX XXX XXXX (24/7 for urgent issues only)</li>
          </ul>
        </SubSection>

        <SubSection title="Help Resources">
          <ul className="list-disc pl-5">
            <li><strong>Knowledge Base:</strong> Comprehensive articles and guides</li>
            <li><strong>Video Tutorials:</strong> Step-by-step visual instructions</li>
            <li><strong>FAQs:</strong> Answers to common questions</li>
            <li><strong>Community Forum:</strong> Connect with other RideMe users</li>
            <li><strong>Blog:</strong> Tips, updates, and platform news</li>
          </ul>
        </SubSection>

        <SubSection title="Provider-Specific Support">
          <ul className="list-disc pl-5 space-y-1">
            <li>Dedicated provider support team available via in-app chat or email</li>
            <li>Vehicle listing optimization assistance to maximize bookings</li>
            <li>Professional photography services (available in select areas, contact support for details)</li>
            <li>Business analytics and performance reporting for rental insights</li>
            <li>Tax documentation assistance for seamless financial reporting</li>
          </ul>
          <p className="mt-2 text-sm text-gray-600">
            For more details on provider tools, visit our{" "}
            <a href="https://rideme.com/provider-hub" className="text-primary-600 hover:underline">
              Provider Hub
            </a>.
          </p>
        </SubSection>
      </Section>

      <Section id="terms" title="Terms & Legal Information" icon={<FileText className="h-6 w-6 text-primary-600" />}>
        <SubSection title="User Agreements">
          <ul className="list-disc pl-5">
            <li><strong>Terms of Service:</strong> General platform usage agreement</li>
            <li><strong>Privacy Policy:</strong> How we handle and protect your data</li>
            <li><strong>Rental Agreement:</strong> Legal contract for each booking</li>
            <li><strong>Provider Agreement:</strong> Specific terms for car providers</li>
          </ul>
          <p className="mt-2">All users must accept these agreements during registration or before relevant actions.</p>
        </SubSection>

        <SubSection title="Privacy & Data Security">
          <ul className="list-disc pl-5">
            <li>Personal data is encrypted and stored securely</li>
            <li>Data sharing only with authorized parties (e.g., insurance providers)</li>
            <li>Regular security audits and compliance checks</li>
            <li>Options to control data usage and marketing preferences</li>
            <li>Right to request data deletion (subject to legal retention requirements)</li>
          </ul>
        </SubSection>

        <SubSection title="Regulatory Compliance">
          <ul className="list-disc pl-5">
            <li>Adherence to local transportation regulations</li>
            <li>Compliance with data protection laws</li>
            <li>Regular updates to meet changing legal requirements</li>
            <li>Tax reporting assistance for providers</li>
          </ul>
        </SubSection>

        <SubSection title="Dispute Resolution">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Direct resolution between parties via in-app messaging</li>
            <li>RideMe mediation if needed</li>
            <li>Formal dispute resolution process</li>
            <li>Third-party arbitration for unresolved issues</li>
          </ol>
        </SubSection>
      </Section>

      <Section id="technical" title="Technical Information" icon={<MessageCircle className="h-6 w-6 text-primary-600" />}>
        <SubSection title="Platform Compatibility">
          <ul className="list-disc pl-5">
            <li><strong>Mobile Applications:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>iOS: Version 12.0 or higher</li>
                <li>Android: Version 8.0 or higher</li>
                <li>App size: Approximately 60MB</li>
              </ul>
            </li>
            <li><strong>Web Platform:</strong>
              <ul className="list-disc pl-5 mt-1">
                <li>Supported browsers: Chrome, Safari, Firefox, Edge (latest versions)</li>
                <li>Responsive design for desktop and mobile viewing</li>
              </ul>
            </li>
          </ul>
        </SubSection>

        <SubSection title="Internet & Location Requirements">
          <ul className="list-disc pl-5">
            <li>Stable internet connection required for optimal experience</li>
            <li>Location services must be enabled for:
              <ul className="list-disc pl-5 mt-1">
                <li>Vehicle search based on location</li>
                <li>Navigation to pickup/return locations</li>
                <li>Emergency assistance features</li>
              </ul>
            </li>
            <li>Offline mode available for accessing booking details</li>
          </ul>
        </SubSection>

        <SubSection title="App Permissions">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b text-left">Permission</th>
                <th className="py-2 px-4 border-b text-left">Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4 border-b">Camera</td>
                <td className="py-2 px-4 border-b">Document scanning, verification photos, vehicle inspection</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">Location</td>
                <td className="py-2 px-4 border-b">Vehicle search, navigation, emergency assistance</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">Notifications</td>
                <td className="py-2 px-4 border-b">Booking updates, messages, reminders</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">Storage</td>
                <td className="py-2 px-4 border-b">Saving documents and photos locally</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border-b">Contacts</td>
                <td className="py-2 px-4 border-b">Optional: Referring friends (with permission)</td>
              </tr>
            </tbody>
          </table>
        </SubSection>

        <SubSection title="Data Usage">
          <ul className="list-disc pl-5">
            <li>Average data usage: 5-15MB per day with regular use</li>
            <li>Higher usage during document uploads and photo captures</li>
            <li>Data-saving mode available in app settings</li>
            <li>Option to restrict data usage to Wi-Fi only</li>
          </ul>
        </SubSection>
      </Section>

      <div className="mt-12 p-6 bg-primary-50 rounded-lg">
        <h2 className="text-xl font-semibold text-primary-800 mb-3">Conclusion</h2>
        <p className="mb-4">
          RideMe aims to provide a secure and seamless car rental experience by connecting customers with trusted car providers 
          while ensuring transparency throughout the process. Our comprehensive platform handles every aspect of the rental journey, 
          from finding the perfect vehicle to completing the rental and providing support when needed.
        </p>
        <p>
          We're constantly improving our platform based on user feedback. If you have any questions or suggestions not covered 
          in this documentation, please contact our support team through the app or at support@rideme.com.
        </p>
      </div>
    </div>
  );
}